import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { organizationsApi } from '@/api/organizations.api';
import { AppShell } from '@/components/layout/AppShell';
import { formatAuditAction, formatDateTime } from '@/lib/format';
import { useAuthStore } from '@/stores/auth.store';

const ACTION_VALUES = [
  '',
  'envelope.created',
  'envelope.sent',
  'envelope.signed',
  'envelope.approved',
  'envelope.declined',
  'envelope.completed',
  'envelope.voided',
  'envelope.viewed',
  'envelope.reminded',
  'envelope.reminded.auto',
  'document.uploaded',
  'document.deleted',
  'template.created',
  'template.deleted',
  'organization.updated',
];

export function AuditLogPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['audit', page, action, search],
    queryFn: () =>
      organizationsApi.listAudit({
        page,
        perPage: 50,
        action: action || undefined,
        search: search || undefined,
      }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / 50));

  // History is an org-wide, admin-only view (mirrors Algebra). Non-admins bounce home.
  if (user && user.role !== 'owner' && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">{t('nav.history')}</h1>
        <p className="page-subtitle">{t('settings.audit_subtitle')}</p>
      </div>

      <div className="card !p-3 mb-4 flex gap-3 flex-wrap items-center">
        <select
          className="input !py-1.5 max-w-[260px] text-[13px]"
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setPage(1);
          }}
        >
          {ACTION_VALUES.map((value) => (
            <option key={value} value={value}>
              {value === '' ? formatAuditAction('all') : formatAuditAction(value)}
            </option>
          ))}
        </select>
        <input
          className="input !py-1.5 max-w-[280px] text-[13px]"
          placeholder={t('admin.filter_by_email')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <span className="ml-auto text-[12px] text-muted">
          {total.toLocaleString()} {t('admin.events')}
        </span>
      </div>

      <div className="card !p-0">
        {isLoading ? (
          <div className="text-center py-12 text-muted">{t('common.loading')}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-muted">{t('admin.no_events')}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('admin.when')}</th>
                <th>{t('admin.action')}</th>
                <th>{t('admin.actor')}</th>
                <th>{t('admin.resource')}</th>
                <th>{t('admin.metadata')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((log) => (
                <tr key={log.id}>
                  <td className="text-[12px] text-muted whitespace-nowrap">
                    {formatDateTime(log.createdAt)}
                  </td>
                  <td>
                    <div className="text-[13px] text-gray-900">{formatAuditAction(log.action)}</div>
                    <code className="text-[10px] text-muted">{log.action}</code>
                  </td>
                  <td className="text-[12px] text-gray-700">{log.actorEmail ?? <span className="text-muted">—</span>}</td>
                  <td className="text-[12px] text-muted">
                    {log.resourceType ? `${log.resourceType}:${log.resourceId}` : '—'}
                  </td>
                  <td className="text-[12px] text-muted max-w-[300px] truncate">
                    {log.metadata ? JSON.stringify(log.metadata) : <span className="text-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {lastPage > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="btn btn-secondary btn-sm"
          >{t('common.back')}</button>
          <span className="text-[13px] text-muted px-3 self-center">
            {t('common.page')} {page} / {lastPage}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            disabled={page >= lastPage}
            className="btn btn-secondary btn-sm"
          >{t('common.next')}</button>
        </div>
      )}
    </AppShell>
  );
}
